import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Image, Play, Globe, Users, Lock, Star, Share2, Facebook, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { API_URL } from '../config/api';
import { searchMulti, getImageUrl } from '../services/tmdb';

const ReviewModal = ({ user, isOpen, onClose, onPostCreated, clubs = [] }) => {
  const [step, setStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [mediaDisplay, setMediaDisplay] = useState('poster');
  const [trailerUrl, setTrailerUrl] = useState(null);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [visibility, setVisibility] = useState('public');
  const [selectedClubId, setSelectedClubId] = useState('');
  const [shareToFacebook, setShareToFacebook] = useState(false);
  const [posting, setPosting] = useState(false);
  const searchTimeout = useRef(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSearchQuery('');
      setSearchResults([]);
      setSelectedContent(null);
      setMediaDisplay('poster');
      setTrailerUrl(null);
      setReviewText('');
      setRating(0);
      setVisibility('public');
      setSelectedClubId('');
      setShareToFacebook(false);
    }
  }, [isOpen]);

  // Debounced search
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const results = await searchMulti(query);
      setSearchResults(results.filter(r => r.media_type === 'movie' || r.media_type === 'tv').slice(0, 8));
    } catch (err) {
      console.error('Search failed:', err);
    }
    setSearching(false);
  }, []);

  const onSearchInput = (value) => {
    setSearchQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(value), 400);
  };

  const selectContent = async (item) => {
    setSelectedContent({
      id: item.id,
      title: item.title || item.name,
      poster_path: item.poster_path,
      media_type: item.media_type,
      overview: item.overview,
      year: (item.release_date || item.first_air_date || '').substring(0, 4),
      vote_average: item.vote_average,
    });
    setSearchResults([]);
    setSearchQuery('');
    setStep(2);

    // Fetch trailer
    try {
      const res = await axios.get(`${API_URL}/api/tmdb/videos/${item.media_type}/${item.id}`);
      if (res.data?.url) setTrailerUrl(res.data.url);
    } catch (e) { /* no trailer */ }
  };

  const handlePost = async () => {
    if (!selectedContent || !reviewText.trim()) return;
    if (visibility === 'club' && !selectedClubId) return;
    setPosting(true);
    try {
      const payload = {
        content: reviewText.trim(),
        content_id: selectedContent.id,
        content_title: selectedContent.title,
        content_poster: selectedContent.poster_path || '',
        content_trailer: trailerUrl || '',
        media_display: mediaDisplay,
        content_type: selectedContent.media_type,
        visibility,
        club_id: visibility === 'club' ? selectedClubId : null,
        external_shared: shareToFacebook,
        rating: rating > 0 ? rating : null,
      };
      await axios.post(`${API_URL}/api/posts?user_id=${user.id}`, payload);

      // Open Facebook share if selected
      if (shareToFacebook && visibility !== 'private') {
        const fbText = encodeURIComponent(`${selectedContent.title} - ${reviewText.substring(0, 100)}`);
        const fbUrl = encodeURIComponent(`https://casitaplex.tv`);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${fbUrl}&quote=${fbText}`, '_blank', 'width=600,height=400');
      }

      onPostCreated?.();
      onClose();
    } catch (err) {
      console.error('Post failed:', err);
    }
    setPosting(false);
  };

  const renderStars = () => {
    const active = hoverRating || rating;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(s => {
          const full = active >= s;
          const half = !full && active >= s - 0.5;
          return (
            <button
              key={s}
              type="button"
              className="p-0.5 relative"
              onMouseLeave={() => setHoverRating(0)}
              data-testid={`rating-star-${s}`}
            >
              {/* Left half — sets s - 0.5 */}
              <div
                className="absolute inset-0 w-1/2 z-10 cursor-pointer"
                onMouseEnter={() => setHoverRating(s - 0.5)}
                onClick={() => setRating(rating === s - 0.5 ? 0 : s - 0.5)}
              />
              {/* Right half — sets s */}
              <div
                className="absolute inset-0 left-1/2 w-1/2 z-10 cursor-pointer"
                onMouseEnter={() => setHoverRating(s)}
                onClick={() => setRating(rating === s ? 0 : s)}
              />
              {/* Star icon */}
              <div className="relative w-5 h-5 sm:w-6 sm:h-6">
                {/* Background (empty) star */}
                <Star className="absolute inset-0 w-full h-full text-zinc-600" />
                {/* Filled portion via clip */}
                {(full || half) && (
                  <div className="absolute inset-0 overflow-hidden" style={{ width: full ? '100%' : '50%' }}>
                    <Star className="w-5 h-5 sm:w-6 sm:h-6 fill-yellow-400 text-yellow-400" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
        {rating > 0 && <span className="text-yellow-400 text-xs ml-1.5 self-center font-medium">{rating}/5</span>}
      </div>
    );
  };

  const visibilityOptions = [
    { id: 'public', label: 'Public', desc: 'Visible to everyone', icon: Globe, color: 'text-green-400' },
    { id: 'followers', label: 'Followers', desc: 'Only your followers', icon: Users, color: 'text-blue-400' },
    { id: 'club', label: 'Club', desc: 'Select a club', icon: Users, color: 'text-purple-400' },
    { id: 'private', label: 'Private', desc: 'Chronicle only', icon: Lock, color: 'text-gray-400' },
  ];

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-lg bg-zinc-900 text-white border-zinc-700 max-h-[85dvh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">Create Review</DialogTitle>
        <DialogDescription className="sr-only">Write a review for a movie or TV show</DialogDescription>

        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 z-10 px-3 sm:px-4 pt-3 sm:pt-4 pb-2.5 sm:pb-3 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-white text-sm" data-testid="review-back-btn">&larr;</button>
            )}
            <h2 className="text-sm sm:text-base font-bold">
              {step === 1 ? 'Select Movie / Show' : step === 2 ? 'Write Review' : 'Review & Share'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {[1, 2, 3].map(s => (
                <div key={s} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full ${step >= s ? 'bg-red-500' : 'bg-zinc-600'}`} />
              ))}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white ml-1 sm:ml-2" data-testid="review-close-btn">
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>

        <div className="px-3 sm:px-4 py-2.5 sm:py-3">
          {/* STEP 1: Search */}
          {step === 1 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  value={searchQuery}
                  onChange={(e) => onSearchInput(e.target.value)}
                  placeholder="Search movies or TV shows..."
                  className="bg-zinc-800 border-zinc-700 text-white h-11 pl-10"
                  autoFocus
                  data-testid="review-search-input"
                />
              </div>
              {searching && (
                <div className="flex items-center justify-center py-6">
                  <div className="animate-spin h-5 w-5 border-2 border-red-600 border-t-transparent rounded-full" />
                </div>
              )}
              <div className="space-y-1 max-h-[45vh] overflow-y-auto">
                {searchResults.map(item => (
                  <button
                    key={`${item.media_type}-${item.id}`}
                    onClick={() => selectContent(item)}
                    className="w-full flex items-center gap-2.5 sm:gap-3 p-2 rounded-lg hover:bg-zinc-800 transition-colors text-left"
                    data-testid={`search-result-${item.id}`}
                  >
                    <div className="w-9 h-12 sm:w-10 sm:h-14 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                      {item.poster_path ? (
                        <img src={getImageUrl(item.poster_path, 'w92')} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-zinc-500"><Image className="h-4 w-4" /></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-xs sm:text-sm font-medium truncate">{item.title || item.name}</p>
                      <p className="text-gray-500 text-[10px] sm:text-xs">
                        {(item.release_date || item.first_air_date || '').substring(0, 4)}
                        {' '}&middot;{' '}{item.media_type === 'movie' ? 'Movie' : 'TV Show'}
                        {item.vote_average > 0 && <span> &middot; {item.vote_average.toFixed(1)}</span>}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">No results found for "{searchQuery}"</p>
              )}
              {!searchQuery && (
                <p className="text-gray-500 text-sm text-center py-6">Start typing to search for a movie or TV show</p>
              )}
            </div>
          )}

          {/* STEP 2: Review + Media + Rating */}
          {step === 2 && selectedContent && (
            <div className="space-y-3 sm:space-y-4">
              {/* Selected content card */}
              <div className="flex gap-2.5 sm:gap-3 bg-zinc-800 rounded-lg p-2.5 sm:p-3">
                <div className="w-12 h-18 sm:w-16 sm:h-24 bg-zinc-700 rounded overflow-hidden flex-shrink-0">
                  {selectedContent.poster_path ? (
                    <img src={getImageUrl(selectedContent.poster_path, 'w154')} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-500"><Image className="h-5 w-5 sm:h-6 sm:w-6" /></div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-white font-semibold text-xs sm:text-sm">{selectedContent.title}</h3>
                  <p className="text-gray-500 text-[10px] sm:text-xs">{selectedContent.year} &middot; {selectedContent.media_type === 'movie' ? 'Movie' : 'TV Show'}</p>
                  <button onClick={() => { setSelectedContent(null); setStep(1); }} className="text-red-500 text-[10px] sm:text-xs mt-1 hover:underline" data-testid="review-change-btn">Change</button>
                </div>
              </div>

              {/* Media type toggle */}
              <div>
                <p className="text-gray-400 text-[10px] sm:text-xs mb-1.5 font-medium uppercase tracking-wide">Display</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setMediaDisplay('poster')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-medium border transition-colors ${
                      mediaDisplay === 'poster' ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-zinc-700 text-gray-400 hover:border-zinc-500'
                    }`}
                    data-testid="media-poster-btn"
                  >
                    <Image className="h-3 w-3 sm:h-3.5 sm:w-3.5" />Poster
                  </button>
                  <button
                    onClick={() => setMediaDisplay('trailer')}
                    disabled={!trailerUrl}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 sm:py-2 rounded-lg text-[11px] sm:text-xs font-medium border transition-colors ${
                      mediaDisplay === 'trailer' ? 'border-red-500 bg-red-500/10 text-red-400' :
                      !trailerUrl ? 'border-zinc-800 text-zinc-600 cursor-not-allowed' :
                      'border-zinc-700 text-gray-400 hover:border-zinc-500'
                    }`}
                    data-testid="media-trailer-btn"
                  >
                    <Play className="h-3 w-3 sm:h-3.5 sm:w-3.5" />Trailer{!trailerUrl && ' (N/A)'}
                  </button>
                </div>
              </div>

              {/* Media preview */}
              {mediaDisplay === 'poster' && selectedContent.poster_path && (
                <div className="rounded-lg overflow-hidden bg-zinc-800 flex justify-center">
                  <img src={getImageUrl(selectedContent.poster_path, 'w342')} alt="" className="max-h-36 sm:max-h-48 object-contain" />
                </div>
              )}
              {mediaDisplay === 'trailer' && trailerUrl && (
                <div className="rounded-lg overflow-hidden bg-black aspect-video">
                  <iframe src={trailerUrl} title="Trailer" className="w-full h-full" allowFullScreen />
                </div>
              )}

              {/* Rating */}
              <div>
                <p className="text-gray-400 text-[10px] sm:text-xs mb-1.5 font-medium uppercase tracking-wide">Rating (optional)</p>
                {renderStars()}
              </div>

              {/* Review text */}
              <div>
                <p className="text-gray-400 text-[10px] sm:text-xs mb-1.5 font-medium uppercase tracking-wide">Your Review</p>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder={`What did you think of ${selectedContent.title}?`}
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[80px] sm:min-h-[100px] resize-none text-xs sm:text-sm"
                  data-testid="review-text-input"
                />
              </div>

              <Button
                onClick={() => setStep(3)}
                disabled={!reviewText.trim()}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-9 sm:h-10 text-xs sm:text-sm"
                data-testid="review-next-btn"
              >
                Next: Visibility & Share
              </Button>
            </div>
          )}

          {/* STEP 3: Visibility + Share */}
          {step === 3 && (
            <div className="space-y-3 sm:space-y-4">
              {/* Summary */}
              <div className="flex gap-2.5 sm:gap-3 bg-zinc-800 rounded-lg p-2.5 sm:p-3">
                {selectedContent?.poster_path && (
                  <img src={getImageUrl(selectedContent.poster_path, 'w92')} alt="" className="w-9 h-12 sm:w-10 sm:h-14 object-cover rounded flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-white font-semibold text-xs sm:text-sm truncate">{selectedContent?.title}</p>
                  <p className="text-gray-400 text-[10px] sm:text-xs truncate">{reviewText.substring(0, 60)}{reviewText.length > 60 ? '...' : ''}</p>
                  {rating > 0 && (
                    <div className="flex items-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map(s => {
                        const full = rating >= s;
                        const half = !full && rating >= s - 0.5;
                        return (
                          <div key={s} className="relative w-3 h-3">
                            <Star className="absolute inset-0 w-full h-full text-zinc-600" />
                            {(full || half) && (
                              <div className="absolute inset-0 overflow-hidden" style={{ width: full ? '100%' : '50%' }}>
                                <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <span className="text-yellow-400 text-[10px] ml-1">{rating}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Visibility */}
              <div>
                <p className="text-gray-400 text-[10px] sm:text-xs mb-1.5 sm:mb-2 font-medium uppercase tracking-wide">Who can see this?</p>
                <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                  {visibilityOptions.map(opt => {
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => setVisibility(opt.id)}
                        className={`flex items-center gap-1.5 sm:gap-2 p-2 sm:p-2.5 rounded-lg border text-left transition-colors ${
                          visibility === opt.id ? 'border-red-500 bg-red-500/10' : 'border-zinc-700 hover:border-zinc-500'
                        }`}
                        data-testid={`visibility-${opt.id}`}
                      >
                        <Icon className={`h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0 ${visibility === opt.id ? 'text-red-400' : opt.color}`} />
                        <div className="min-w-0">
                          <p className={`text-[11px] sm:text-xs font-medium truncate ${visibility === opt.id ? 'text-white' : 'text-gray-300'}`}>{opt.label}</p>
                          <p className="text-[9px] sm:text-[10px] text-gray-500 truncate">{opt.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Club selector */}
              {visibility === 'club' && (
                <div>
                  <p className="text-gray-400 text-xs mb-1.5 font-medium uppercase tracking-wide">Select Club</p>
                  <select
                    value={selectedClubId}
                    onChange={(e) => setSelectedClubId(e.target.value)}
                    className="w-full bg-zinc-800 border border-zinc-700 text-white rounded-lg h-10 px-3 text-sm"
                    data-testid="club-select"
                  >
                    <option value="">Choose a club...</option>
                    {clubs.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              {/* Share destinations */}
              {visibility !== 'private' && (
                <div>
                  <p className="text-gray-400 text-[10px] sm:text-xs mb-1.5 sm:mb-2 font-medium uppercase tracking-wide">Also share to</p>
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-center justify-between p-2 sm:p-2.5 rounded-lg bg-zinc-800 border border-zinc-700">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-red-600 flex items-center justify-center">
                          <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                        </div>
                        <span className="text-xs sm:text-sm text-white">CasitaPlex</span>
                      </div>
                      <span className="text-green-400 text-xs">Always</span>
                    </div>
                    <button
                      onClick={() => setShareToFacebook(!shareToFacebook)}
                      className={`w-full flex items-center justify-between p-2 sm:p-2.5 rounded-lg border transition-colors ${
                        shareToFacebook ? 'bg-blue-600/10 border-blue-500' : 'bg-zinc-800 border-zinc-700 hover:border-zinc-500'
                      }`}
                      data-testid="share-facebook-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center ${shareToFacebook ? 'bg-blue-600' : 'bg-zinc-600'}`}>
                          <Facebook className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-white" />
                        </div>
                        <span className="text-xs sm:text-sm text-white">Facebook</span>
                      </div>
                      <div className={`w-9 h-5 rounded-full transition-colors ${shareToFacebook ? 'bg-blue-600' : 'bg-zinc-600'}`}>
                        <div className={`w-4 h-4 rounded-full bg-white mt-0.5 transition-transform ${shareToFacebook ? 'translate-x-4.5 ml-[18px]' : 'ml-0.5'}`} />
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {visibility === 'private' && (
                <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 flex items-start gap-2">
                  <Lock className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-400 text-xs">This review will only be saved to your Chronicle. It won't appear in any feed.</p>
                </div>
              )}

              {/* Post button */}
              <Button
                onClick={handlePost}
                disabled={posting || (visibility === 'club' && !selectedClubId)}
                className="w-full bg-red-600 hover:bg-red-700 text-white h-9 sm:h-11 text-xs sm:text-sm font-semibold"
                data-testid="review-post-btn"
              >
                {posting ? 'Posting...' : visibility === 'private' ? 'Save to Chronicle' : 'Post Review'}
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewModal;
